// CaptainUp secure auth contract.
//
// This module is the single source of truth for:
// - the backend response shape we expect
// - the request body format (JSON)
// - how we validate/sanitize the response

export type CaptainUpSecureAuth = {
	user: {
		id: number | string;
		name?: string;
		image?: string;
	};
	signedUser: string;
	brandId: number;
	cluster: string;
};

export type CaptainUpAuthResponse = {
	captainUp: CaptainUpSecureAuth;
};

export type CaptainUpAuthFetchMode = "mock" | "network";

export type CaptainUpAuthServiceOptions =
	| {
			mode: "mock";
			mock: (sessionKey: string) => Promise<CaptainUpAuthResponse>;
	  }
	| {
			mode: "network";
			endpointUrl: string;
	  };

const CAPTAIN_UP_AUTH_TIMEOUT_MS = 5000;

export type CaptainUpAuthInitErrorKind =
	| "TIMEOUT"
	| "BAD_REQUEST"
	| "NOT_FOUND"
	| "SERVER_ERROR"
	| "NETWORK_ERROR"
	| "INVALID_RESPONSE";

export class CaptainUpAuthInitError extends Error {
	public readonly kind: CaptainUpAuthInitErrorKind;
	public readonly status?: number;

	public constructor(kind: CaptainUpAuthInitErrorKind, message: string, status?: number) {
		super(message);
		this.name = "CaptainUpAuthInitError";
		this.kind = kind;
		this.status = status;
	}
}

function ensureString(value: unknown): string {
	return typeof value === "string" ? value : "";
}

function ensureNonEmptyString(value: unknown, errorMessage: string): string {
	const s = ensureString(value).trim();
	if (!s) {
		throw new CaptainUpAuthInitError("INVALID_RESPONSE", errorMessage);
	}
	return s;
}

function ensureUserId(value: unknown): number | string {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string" && value.trim().length > 0) return value.trim();

	throw new CaptainUpAuthInitError("INVALID_RESPONSE", "[CaptainUpAuthService] invalid captainUp.user.id");
}

function ensureBrandId(value: unknown): number {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	throw new CaptainUpAuthInitError("INVALID_RESPONSE", "[CaptainUpAuthService] invalid response: captainUp.brandId missing");
}

function coerceOptionalString(value: unknown): string | undefined {
	const s = ensureString(value).trim();
	return s.length > 0 ? s : undefined;
}

function sanitizeResponseOrThrow(raw: unknown): CaptainUpAuthResponse {
	const obj = raw as any;

	if (!obj || typeof obj !== "object") {
		throw new CaptainUpAuthInitError("INVALID_RESPONSE", "[CaptainUpAuthService] invalid response: not an object");
	}

	const captainUp = obj.captainUp;
	if (!captainUp || typeof captainUp !== "object") {
		throw new CaptainUpAuthInitError("INVALID_RESPONSE", "[CaptainUpAuthService] invalid response: missing captainUp");
	}

	const signedUser = ensureNonEmptyString(
		captainUp.signedUser,
		"[CaptainUpAuthService] invalid response: captainUp.signedUser missing"
	);

	const brandId = ensureBrandId(captainUp.brandId);

	const cluster = ensureNonEmptyString(
		captainUp.cluster,
		"[CaptainUpAuthService] invalid response: captainUp.cluster missing"
	);

	const user = captainUp.user;
	if (!user || typeof user !== "object") {
		throw new CaptainUpAuthInitError("INVALID_RESPONSE", "[CaptainUpAuthService] invalid response: captainUp.user missing");
	}

	const id = ensureUserId(user.id);

	const name = coerceOptionalString(user.name);
	const image = coerceOptionalString(user.image);

	return {
		captainUp: {
			user: { id, name, image },
			signedUser,
			brandId,
			cluster,
		},
	};
}

function mapHttpErrorToCaptainUpAuthError(status: number, statusText: string): CaptainUpAuthInitError {
	const message = `[CaptainUpAuthService] auth request failed: ${status} ${statusText}`;

	if (status === 400) {
		return new CaptainUpAuthInitError("BAD_REQUEST", message, status);
	}

	if (status === 404) {
		return new CaptainUpAuthInitError("NOT_FOUND", message, status);
	}

	if (status >= 500 && status <= 599) {
		return new CaptainUpAuthInitError("SERVER_ERROR", message, status);
	}

	return new CaptainUpAuthInitError("NETWORK_ERROR", message, status);
}

export async function fetchCaptainUpAuthOrThrow(
	sessionKey: string,
	options: CaptainUpAuthServiceOptions
): Promise<CaptainUpAuthResponse> {
	const key = String(sessionKey ?? "").trim();
	if (!key) {
		throw new CaptainUpAuthInitError("BAD_REQUEST", "[CaptainUpAuthService] missing sessionKey", 400);
	}

	if (options.mode === "mock") {
		const raw = await options.mock(key);
		return sanitizeResponseOrThrow(raw);
	}

	const controller = new AbortController();
	const timeoutId = window.setTimeout(() => {
		controller.abort();
	}, CAPTAIN_UP_AUTH_TIMEOUT_MS);

	try {
		// Network mode (real backend):
		// IMPORTANT:
		// - body is JSON, so it MUST be JSON.stringify(...)
		// - server reads { sessionKey }
		const res = await fetch(options.endpointUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ sessionKey: key }),
			signal: controller.signal,
		});

		if (!res.ok) {
			throw mapHttpErrorToCaptainUpAuthError(res.status, res.statusText);
		}

		const json = (await res.json()) as unknown;
		return sanitizeResponseOrThrow(json);
	} catch (error) {
		if (error instanceof CaptainUpAuthInitError) {
			throw error;
		}

		if (error instanceof DOMException && error.name === "AbortError") {
			throw new CaptainUpAuthInitError("TIMEOUT", "[CaptainUpAuthService] auth request timed out");
		}

		const message = error instanceof Error ? error.message : String(error);
		throw new CaptainUpAuthInitError("NETWORK_ERROR", `[CaptainUpAuthService] auth request failed: ${message}`);
	} finally {
		window.clearTimeout(timeoutId);
	}
}
